import { ActivitySpinnerWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React, { ReactElement } from 'react';
import { connect } from 'react-redux';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { IGetSiteEncryptionKeyRootKey } from '~src/settings/security-settings/sections/extract-encryption/models/ExtractEncryptionModels';
import { ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { cellStyle, headerStyle, rowStyle, tableStyle } from '../styles/ExtractEncryptionSettingsStyles';
import { capitalizeFirstLetter, formatDate, getTooltip } from '../utils/ExtractEncrpytionUtils';
import { KMSEncryptionKeyActionMenu } from './KMSEncrpytionKeyActionMenu';

export interface KMSEncryptionKeyTableOwnProps {
  siteSettings: ISiteSettingsForSiteAdmin;
}

export interface KMSEncryptionKeyTableStateProps {
  isLoading: boolean;
  kmsKeys: IGetSiteEncryptionKeyRootKey[];
}

export type KMSEncryptionKeyTableProps = KMSEncryptionKeyTableOwnProps & KMSEncryptionKeyTableStateProps;


interface ColumnKey {
  name: string;
  width: string;
  render: (key: IGetSiteEncryptionKeyRootKey, siteSettings: ISiteSettingsForSiteAdmin) => ReactElement;
}

export function getColumnKeys(): ColumnKey[]{
  return [
    {
      name: Messages.extractEncryptionKMS_Identifier_Header(),
      width: '20%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(key.keyId)
    },
    {
      name: Messages.actions(),
      width: '5%',
      render: (key: IGetSiteEncryptionKeyRootKey, siteSettings: ISiteSettingsForSiteAdmin) => <KMSEncryptionKeyActionMenu  kmsEncrpytionKey={key} siteSettings={siteSettings}/>
    },
    {
      name: Messages.extractEncryptionKMS_RootKeyType_Header(),
      width: '10%',
      render: (key: IGetSiteEncryptionKeyRootKey) => <div>AWS</div>
    },
    {
      name: Messages.extractEncryptionKMS_Status_Header(),
      width: '10%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(capitalizeFirstLetter(key.keyStatus))
    },
    {
      name: Messages.extractEncryptionKMS_KMSIdentifier_Header(),
      width: '20%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(key.externalKeyId)
    },
    {
      name: Messages.extractEncryptionKMS_Description_Header(),
      width: '15%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(key.keyDescription)
    },
    {
      name: Messages.extractEncryptionKMS_Generated_Header(),
      width: '10%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(formatDate(key.keyCreatedDate))
    },
    {
      name: Messages.extractEncryptionKMS_Modified_Header(),
      width: '10%',
      render: (key: IGetSiteEncryptionKeyRootKey) => getTooltip(formatDate(key.lastModifiedDate))
    }
  ];
  }

export class KMSEncryptionKeyTableComponent extends React.PureComponent<KMSEncryptionKeyTableProps, {}> {

  public constructor(props: KMSEncryptionKeyTableProps) {
    super(props);
  }

  public render() {
    if(this.props.isLoading){
      return(
        <div style={{paddingRight: '50px'}}>
          <table style={tableStyle}>
            {this.getTableHeaders()}
          </table>
            <div data-tb-test-id style={{textAlign: 'center'}}>
              <ActivitySpinnerWidget style={{textAlign:'center', marginTop: '30px'}} testId='test'></ActivitySpinnerWidget>
            </div>
        </div>
      );
    } else{
      return (
        <div style={{paddingRight: '50px'}}>
          <table style={tableStyle}>
            {this.getTableHeaders()}
            <tbody>
              {this.props.kmsKeys.map((key, index) => (
                <React.Fragment key={index}>
                  <tr style={rowStyle}>
                    {getColumnKeys().map((header, headerIndex) => (
                      <td key={headerIndex} style={{ ...cellStyle, width: header.width }}>
                      {header.render(key, this.props.siteSettings)}
                    </td>
                    ))}
                  </tr>
                  {this.getInlineAlert(key)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }

  private getTableHeaders() {
      return (
        <thead>
          <tr style={rowStyle}>
            {getColumnKeys().map((header, index) => (
              <th key={index} style={{ ...headerStyle, width: header.width }}>
                {getTooltip(header.name)}
              </th>
            ))}
          </tr>
        </thead>
      );
    }

  private getInlineAlert(kmsKey: IGetSiteEncryptionKeyRootKey) {
    if(kmsKey.keyStatus === 'PENDING'){
      return (
        <tr>
          <td style={{paddingTop: '10px', paddingBottom: '15px'}} colSpan={getColumnKeys().length}>
          <InlineAlertWidget testId='kms-key-pending-alert' type={InlineAlertType.Info}>
              <span>{Messages.extractEncryption_KMSKeyPendingAlert()}</span>
          </InlineAlertWidget>
          </td>
        </tr>);
      }
    return null;
  }
 }

const mapStateToProps: MapStateToProps<KMSEncryptionKeyTableStateProps> = (state: AppState) => ({
  isLoading: state.extractEncryptionInfo.isFetchingEncryptionKey,
  kmsKeys: state.extractEncryptionInfo.encryptionKey?.rootKeys ?? []
});

export const KMSEncryptionKeyTable: React.ComponentClass<KMSEncryptionKeyTableOwnProps> =
  connect(mapStateToProps, undefined)(KMSEncryptionKeyTableComponent);